// Content of the integration guide shown on Settings -> API Keys.
//
// One source for two outputs: the in-app guide (IntegrationGuide.tsx) and the
// markdown file an admin downloads to send to the partner's developers
// (guideMarkdown.ts). Everything here describes the public API served by the
// api-gateway; keep it in step with `openapi/openapi.yaml` there.
//
// Inline text supports `code`, **bold** and [label](url) only.

import { ApiKeyPermission } from '@services/api-keys.service';
import * as samples from './guideSamples';

export type SampleLang = 'curl' | 'node' | 'python' | 'csharp' | 'json' | 'http';

export interface CodeSample {
  lang: SampleLang;
  code: string;
}

export type GuideBlock =
  | { type: 'p'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'list'; ordered?: boolean; items: string[] }
  | { type: 'note'; tone: 'info' | 'warning'; text: string }
  | { type: 'table'; head: string[]; rows: string[][]; copyColumn?: number }
  | { type: 'code'; samples: CodeSample[] }
  | { type: 'checklist' };

export interface GuideSection {
  id: string;
  title: string;
  /** Scopes the calls in this section need; used to flag a key that lacks them. */
  scopes?: ApiKeyPermission[];
  blocks: GuideBlock[];
}

export interface GuideTeam {
  id: string;
  name: string;
}

export interface GuideContext {
  /** Public api-gateway origin for this environment, no trailing slash. */
  baseUrl: string;
  apiReferenceUrl: string;
  openApiUrl: string;
  /** The selected key, when one is picked. Never the secret. */
  keyName?: string | null;
  productName?: string | null;
  productSlug?: string | null;
  /** Per-key requests/minute override, when the key has one. */
  keyRateLimit?: number | null;
  /** null = teams could not be loaded or were not requested. */
  teams?: GuideTeam[] | null;
}

export const SAMPLE_LANG_LABELS: Record<SampleLang, string> = {
  curl: 'curl',
  node: 'Node.js',
  python: 'Python',
  csharp: 'C#',
  json: 'JSON',
  http: 'HTTP',
};

export const PLACEHOLDER_SOURCE = 'your-system';
export const PLACEHOLDER_TEAM_ID = '00000000-0000-4000-8000-000000000000';
export const EXAMPLE_EXTERNAL_ID = 'CASE-1042';

export type GoLiveCheckId =
  | 'authenticated'
  | 'ticket_created'
  | 'lookup'
  | 'duplicate'
  | 'status_changed'
  | 'webhook_registered'
  | 'webhook_delivered';

export interface GoLiveItem {
  /** Present when CentraOne can confirm the item from the key's activity. */
  checkId?: GoLiveCheckId;
  text: string;
}

export const GO_LIVE_ITEMS: GoLiveItem[] = [
  { checkId: 'authenticated', text: 'An authenticated call has reached the API' },
  { checkId: 'ticket_created', text: 'A ticket has been created with `POST /api/tickets`' },
  { checkId: 'lookup', text: 'A ticket has been fetched by its `externalId`' },
  { checkId: 'duplicate', text: 'A repeated create returned 409 and your code kept the existing ticket id' },
  { checkId: 'status_changed', text: 'A status change has been sent with `PATCH /api/tickets/{id}/status`' },
  { checkId: 'webhook_registered', text: 'A webhook is registered and enabled' },
  { checkId: 'webhook_delivered', text: 'A webhook delivery has been accepted by your endpoint' },
  { text: 'Your receiver verifies `X-Webhook-Signature-V2`, rejects old timestamps and de-duplicates on `X-Webhook-Id`' },
  { text: 'Your client waits for `Retry-After` on 429 and retries 5xx with backoff' },
  { text: 'The polling backstop finds a change made while the webhook was switched off' },
  { text: 'The production key and webhook secret were exchanged over a secure channel' },
];

/** The slug to show in examples: the key's product, else a placeholder. */
export function exampleSource(ctx: GuideContext): string {
  return ctx.productSlug || PLACEHOLDER_SOURCE;
}

/** The team id to show in examples: the tenant's first team, else a placeholder. */
export function exampleTeamId(ctx: GuideContext): string {
  return ctx.teams?.[0]?.id ?? PLACEHOLDER_TEAM_ID;
}

export function buildGuide(ctx: GuideContext): GuideSection[] {
  const source = exampleSource(ctx);
  const hasProduct = !!ctx.productSlug;
  const sampleCtx: samples.SampleContext = {
    baseUrl: ctx.baseUrl,
    source,
    hasProduct,
    teamId: exampleTeamId(ctx),
    externalId: EXAMPLE_EXTERNAL_ID,
  };

  const externalSourceNote = hasProduct
    ? `This key's product is **${ctx.productName}**, so every ticket it creates gets \`origin: ${source}\` and \`externalSource: ${source}\`. Leave \`externalSource\` out of the request; any other value returns 400.`
    : 'A key with a product label sets `externalSource` for you. A key without one must send `externalSource` and `externalId` together.';

  const sections: GuideSection[] = [
    {
      id: 'overview',
      title: 'How the integration works',
      blocks: [
        {
          type: 'table',
          head: ['Direction', 'Mechanism', 'Who builds it'],
          rows: [
            ['Your system → CentraOne', 'You call the REST API with an API key', 'You'],
            ['CentraOne → your system', 'Signed webhooks are POSTed to an HTTPS endpoint you host', 'You host the receiver'],
            ['Backstop', 'You poll for tickets changed since your last check', 'You'],
          ],
        },
        {
          type: 'table',
          head: ['What', 'Value'],
          copyColumn: 1,
          rows: [
            ['Base URL', ctx.baseUrl],
            ['API reference', ctx.apiReferenceUrl],
            ['OpenAPI spec', ctx.openApiUrl],
          ],
        },
        {
          type: 'p',
          text: 'CentraOne does not call your system\'s API and has no receiver for your event format. Everything inbound goes through the REST API.',
        },
      ],
    },
    {
      id: 'quick-start',
      title: 'Quick start',
      scopes: [ApiKeyPermission.TICKETS_READ, ApiKeyPermission.TICKETS_WRITE],
      blocks: [
        {
          type: 'p',
          text: 'Keep the key in an environment variable on your server. The examples read it from `CENTRAONE_API_KEY`.',
        },
        { type: 'heading', text: '1. Create a ticket' },
        { type: 'code', samples: samples.createTicket(sampleCtx) },
        {
          type: 'p',
          text: 'The response is the ticket. Store its `id` against your record; every later call uses it.',
        },
        { type: 'heading', text: '2. Fetch it by your own id' },
        { type: 'code', samples: samples.lookupTicket(sampleCtx) },
        { type: 'heading', text: '3. Change its status' },
        { type: 'code', samples: samples.changeStatus(sampleCtx) },
      ],
    },
    {
      id: 'authentication',
      title: 'Authentication and limits',
      blocks: [
        {
          type: 'code',
          samples: [{ lang: 'http', code: 'Authorization: Bearer cop_…\n\n# or\nX-API-Key: cop_…' }],
        },
        {
          type: 'list',
          items: [
            'The tenant is taken from the key. Never send a tenant id.',
            'Call the base URL above. The web app\'s own API host rejects API keys.',
            'Keep the key on your server. Do not ship it in a browser or mobile app.',
          ],
        },
        {
          type: 'table',
          head: ['Scope', 'Allows'],
          rows: [
            ['`tickets:read`', 'Read tickets, history, comments and attachments'],
            ['`tickets:write`', 'Create and change tickets, add comments, upload attachments'],
            ['`customers:read` / `customers:write`', 'Read / create and change customers'],
            ['`webhooks:read` / `webhooks:write`', 'Read / manage webhook subscriptions and delivery logs'],
            ['`knowledge:read` / `knowledge:write`', 'Knowledge-base articles and categories'],
            ['`analytics:read`', 'Read-only reports'],
          ],
        },
        {
          type: 'table',
          head: ['Topic', 'What to do'],
          rows: [
            ['Content type', '`Content-Type: application/json`. Attachments use `multipart/form-data`'],
            ['API version', 'Optional `Accept-Version: 2026-05-12`. Leave it out for the latest stable version'],
            ['Idempotency', 'Optional `Idempotency-Key` on any write (letters, digits, `_`, `-`; up to 255 characters). A retry within 24 hours replays the first response with `Idempotent-Replay: true`'],
            [
              'Rate limit',
              `Per key: 60 requests per minute on the Growth plan, 600 on Professional.${
                ctx.keyRateLimit ? ` This key is set to ${ctx.keyRateLimit} per minute.` : ''
              } Responses carry \`X-RateLimit-Limit\`, \`X-RateLimit-Remaining\` and \`X-RateLimit-Reset\`. Over the limit you get 429 with \`Retry-After\` in seconds`,
            ],
            ['Pagination', '`page` (from 1) and `limit` (up to 200). Lists return `{ data, total, page, limit }`'],
            ['Test keys', 'A `cop_test_` key returns sample data, touches nothing real and fires no webhooks. Test against a live key on a non-production tenant'],
          ],
        },
      ],
    },
    {
      id: 'create-ticket',
      title: 'Creating a ticket',
      scopes: [ApiKeyPermission.TICKETS_WRITE],
      blocks: [
        { type: 'p', text: '`POST /api/tickets`' },
        {
          type: 'table',
          head: ['Field', 'Required', 'Type', 'Notes'],
          rows: [
            ['`subject`', 'Yes', 'string, up to 500', 'Must not be blank'],
            ['`channel`', 'Yes', 'enum', 'Send `api`. Other values: `email`, `chat`, `phone`, `web`, `portal`, `whatsapp`, `facebook`, `twitter`'],
            ['`description`', 'No', 'string', 'Ticket body. Put a link to your record here if the content stays in your system'],
            ['`priority`', 'No', 'enum', '`low`, `medium` (default), `high`, `urgent`, in lowercase'],
            ['`externalId`', 'Recommended', 'string, up to 255', 'The ticket\'s id in your system. Unique per source'],
            ['`externalSource`', 'See below', 'string, up to 100', 'Lowercase letters, digits, `.`, `_`, `-`'],
            ['`externalUrl`', 'No', 'https URL, up to 2048', 'The ticket\'s page in your system. Agents get a button on the ticket that opens it in a new tab; customers never see it'],
            ['`externalUrlLabel`', 'No', 'string, up to 60', 'The button\'s text. Defaults to "Open in" plus your product name'],
            ['`teamId`', 'No', 'UUID', 'Routes the ticket to that team. An agent of that team is picked by the assignment rules, or the ticket waits unassigned in that team\'s queue'],
            ['`assigneeId`', 'No', 'UUID', 'A specific agent. Skips auto-assignment'],
            ['`customerId`', 'No', 'UUID', 'An existing CentraOne customer'],
            ['`contactEmail`', 'No', 'email', 'Without a `customerId`, the customer with this email is linked, or created'],
            ['`contactName`', 'No', 'string, up to 255', ''],
            ['`tags`', 'No', 'string[]', ''],
            ['`customFields`', 'No', 'object', 'Keys must match custom fields configured on the tenant'],
            ['`ticketTypeId`, `pipelineId`, `stageId`, `slaPolicyId`', 'No', 'UUID', 'Only if you were given these ids. Otherwise the tenant defaults apply'],
            ['`metadata`', 'No', 'object', 'Free-form data stored with the ticket'],
            ['`suppressAcknowledgement`', 'No', 'boolean', '`true` stops the "we received your request" email to the customer'],
          ],
        },
        { type: 'p', text: 'Unknown fields are rejected with `400 validation_error`.' },
        { type: 'note', tone: 'info', text: externalSourceNote },
        {
          type: 'note',
          tone: 'warning',
          text: 'If you send `contactEmail` or `customerId`, replies written by agents are sent to that customer. If the customer must only hear from your system, leave both out.',
        },
        { type: 'heading', text: 'Duplicates' },
        {
          type: 'p',
          text: 'A second create with the same `externalId` returns 409. Treat it as success and store `error.existing_ticket.id`. If `existing_ticket` is missing (two creates racing), fetch the ticket by its external id.',
        },
        { type: 'code', samples: [{ lang: 'json', code: samples.conflictBody(sampleCtx) }] },
      ],
    },
    {
      id: 'tickets',
      title: 'Working with tickets',
      scopes: [ApiKeyPermission.TICKETS_READ, ApiKeyPermission.TICKETS_WRITE],
      blocks: [
        {
          type: 'table',
          head: ['Action', 'Call', 'Body'],
          rows: [
            ['Find by your id', `\`GET /api/tickets/external?externalSource=${source}&externalId={id}\``, ''],
            ['Get', '`GET /api/tickets/{id}`', ''],
            ['List / poll', '`GET /api/tickets`', ''],
            ['Update fields', '`PATCH /api/tickets/{id}`', 'Any create field, e.g. `{ "tags": ["cancelled"] }`'],
            ['Change status', '`PATCH /api/tickets/{id}/status`', '`{ "status": "pending" }`'],
            ['Change priority', '`PATCH /api/tickets/{id}/priority`', '`{ "priority": "urgent" }`'],
            ['Assign or re-route', '`PATCH /api/tickets/{id}/assign`', '`{ "teamId": "…" }` and/or `{ "assigneeId": "…" }`'],
            ['Unassign', '`PATCH /api/tickets/{id}/unassign`', ''],
            ['History', '`GET /api/tickets/{id}/history`', ''],
            ['Delete', '`DELETE /api/tickets/{id}`', ''],
          ],
        },
        {
          type: 'p',
          text: 'List filters: `status`, `assigneeId`, `customerId`, `contactEmail` (exact address; with `customerId`, a ticket matching either is returned), `externalSource`, `externalId`, `origin` (comma-separated), `originType`, `updatedSince`, `sortBy` (`createdAt`, `updatedAt`, `subject`, `priority`), `sortOrder` (`asc`, `desc`).',
        },
        {
          type: 'list',
          items: [
            'Statuses are `new`, `in_progress`, `pending`, `resolved`, `closed`. There is no cancelled status; use `closed` plus a tag.',
            'Changing the status also moves the ticket\'s pipeline stage.',
            'A resolved or closed ticket that is reopened returns to `in_progress`.',
            'URL-encode `externalId` in the lookup; ids containing `/` or `#` work.',
            'A key with a product can only look up or filter by its own `externalSource`. Any other value returns 403.',
            'Add or change the link back to your system with `PATCH /api/tickets/{id}` and `{ "externalUrl": "https://…" }`; send `null` to remove it. It must be https (400 otherwise). A key with a product can only set it on its own tickets (403 otherwise). Unlinking a ticket also removes it.',
          ],
        },
        { type: 'heading', text: 'Comments' },
        {
          type: 'table',
          head: ['Action', 'Call', 'Notes'],
          rows: [
            ['Add', '`POST /api/tickets/{id}/comments`', '`{ "body": "…", "isInternal": true }`. Plain text, up to 50,000 characters'],
            ['List', '`GET /api/tickets/{id}/comments?since={ISO time}&limit=100`', 'Oldest first; includes internal notes'],
          ],
        },
        {
          type: 'note',
          tone: 'warning',
          text: '`isInternal` defaults to `false`, which is a public reply **sent to the customer**. Send `isInternal: true` for a note only agents see. The author is shown as the API key\'s name.',
        },
        { type: 'heading', text: 'Attachments' },
        {
          type: 'table',
          head: ['Action', 'Call'],
          rows: [
            ['Upload', '`POST /api/attachments` as `multipart/form-data` with exactly two parts: `file` and `ticketId`'],
            ['List', '`GET /api/attachments?ticketId={id}`'],
            ['Metadata, download, delete', '`GET /api/attachments/{id}`, `GET /api/attachments/{id}/download`, `DELETE /api/attachments/{id}`'],
          ],
        },
        {
          type: 'p',
          text: 'Maximum 25 MB per file. The type is checked by content against an allow-list (documents, images, archives, audio, video); a rejected file returns 400.',
        },
      ],
    },
    {
      id: 'customers',
      title: 'Customers',
      scopes: [ApiKeyPermission.CUSTOMERS_READ, ApiKeyPermission.CUSTOMERS_WRITE],
      blocks: [
        {
          type: 'p',
          text: 'You only need these if you manage customer records yourself; sending `contactEmail` on a ticket is enough to link or create a customer.',
        },
        {
          type: 'table',
          head: ['Action', 'Call', 'Scope'],
          rows: [
            ['Create', '`POST /api/customers` with `{ "email": "…", "name": "…", "externalId": "<your id>" }`', 'write'],
            ['Find by your id', '`GET /api/customers/by-external-id/{externalId}`', 'read'],
            ['Find by email', '`GET /api/customers/by-email/{email}` (URL-encode: `@` becomes `%40`)', 'read'],
            ['Get', '`GET /api/customers/{id}`', 'read'],
            ['List', '`GET /api/customers?search=&status=&organizationId=&tag=&ids=&updatedSince=&page=&limit=`', 'read'],
            ['Update', '`PATCH /api/customers/{id}` (or `PUT`, the same partial update). Send only the fields to change', 'write'],
            ['Delete', '`DELETE /api/customers/{id}`', 'write'],
            ['The customer\'s tickets', '`GET /api/tickets?customerId={id}`', 'tickets read'],
          ],
        },
        {
          type: 'list',
          items: [
            '`email` is required on create. `email`, `phone` and `externalId` are each unique per tenant; a duplicate returns 409, so look the customer up and update it instead.',
            'Set `externalId` to your own id for the customer, so you never have to store ours.',
            'Send `externalUrl` (https, on create or update) and optionally `externalUrlLabel` to give agents a button on the customer\'s profile that opens their page in your system. Send `null` to remove it.',
            '`status` is `active`, `inactive` or `blocked`.',
            'A customer with `managedBy` set is owned by a linked CRM: its name, email, phone, organization and status are read-only (409 `FIELD_MANAGED_EXTERNALLY`).',
          ],
        },
        { type: 'heading', text: 'Keeping customers in sync both ways' },
        {
          type: 'list',
          ordered: true,
          items: [
            'Your system to CentraOne: create with `externalId`; on later changes find the customer by `externalId` and `PATCH` it.',
            'CentraOne to your system: subscribe to `customer.created` and `customer.updated`. Both carry `customerId`, `externalId` and the current details; `customer.updated` also has `changes` (`{ field: { old, new } }`).',
            'Avoid loops: when an event arrives for a change you just made, skip it if its fields already equal yours.',
            'Deletes and merges: subscribe to `customer.deleted`. `reason` is `deleted` or `merged`; for a merge, `mergedIntoCustomerId` / `mergedIntoExternalId` name the customer that now holds the tickets, so re-point your links to it.',
            'Polling backstop: `GET /api/customers?updatedSince={last updatedAt}&sortBy=updatedAt&sortOrder=asc`. Deleted and merged customers drop out of the list, so only `customer.deleted` reports them.',
          ],
        },
      ],
    },
    {
      id: 'teams',
      title: 'Team IDs',
      blocks: teamBlocks(ctx),
    },
    {
      id: 'webhooks',
      title: 'Receiving changes (webhooks)',
      scopes: [ApiKeyPermission.WEBHOOKS_READ, ApiKeyPermission.WEBHOOKS_WRITE],
      blocks: [
        { type: 'heading', text: 'What your endpoint must do' },
        {
          type: 'list',
          items: [
            'Be reachable over public HTTPS and accept `POST` with a JSON body.',
            'Verify the signature using the **raw request body**, before parsing it.',
            'Return any 2xx within 30 seconds. Do slow work after responding.',
            'Accept the same delivery more than once: de-duplicate on the `X-Webhook-Id` header.',
            'Not depend on order. Use the status and timestamps in the payload, or re-fetch the ticket.',
          ],
        },
        { type: 'heading', text: 'Registering' },
        {
          type: 'p',
          text: 'An admin can add the webhook under Settings → Webhooks, or you can register it with `POST /api/webhooks`:',
        },
        { type: 'code', samples: [{ lang: 'json', code: samples.webhookRegistration() }] },
        {
          type: 'list',
          items: [
            '`name`, `url`, `secret` and `events` are required. `events` must list at least one event; there is no "all events" wildcard.',
            'You choose the secret. Use at least 32 random characters. It is never returned by later reads.',
            'Other calls: `GET /api/webhooks`, `GET` / `PUT` / `DELETE /api/webhooks/{id}`, `GET /api/webhooks/{id}/deliveries`, `POST /api/webhooks/{id}/rotate-secret`.',
          ],
        },
        { type: 'heading', text: 'Events' },
        {
          type: 'table',
          head: ['Event', 'Fires when'],
          rows: [
            ['`ticket.created`', 'A ticket is created'],
            ['`ticket.updated`', 'Ticket fields change'],
            ['`ticket.status_changed`', 'Status changes'],
            ['`ticket.assigned`', 'Agent or team changes'],
            ['`ticket.priority_changed`', 'Priority changes'],
            ['`ticket.resolved`, `ticket.closed`', 'Status becomes `resolved` / `closed`'],
            ['`ticket.reopened`', 'A resolved or closed ticket is reopened'],
            [
              '`ticket.message_added`',
              'A customer-visible message is added (customer, agent or automatic reply), with its full body. Internal notes never fire it',
            ],
            ['`ticket.note_added`', 'An internal note is added, with its full body. Subscribe only if you may hold agent-only notes'],
            ['`customer.created`', 'A customer is created, with `customerId`, `externalId`, `externalUrl` and its details'],
            ['`customer.updated`', 'A customer\'s details change: the same fields after the change, plus `changes`'],
            ['`customer.deleted`', 'A customer is deleted (`reason: deleted`) or merged into another (`reason: merged`, with `mergedIntoCustomerId`)'],
          ],
        },
        { type: 'p', text: 'Every delivery has the same envelope:' },
        { type: 'code', samples: [{ lang: 'json', code: samples.webhookPayload(sampleCtx) }] },
        {
          type: 'list',
          items: [
            '`ticket.resolved`, `ticket.closed` and `ticket.reopened` carry the flat fields shown (with `closedAt`, or `reopenedAt` and `reason`).',
            '`ticket.created`, `ticket.status_changed` and `ticket.assigned` carry the full ticket in `data.entity`, plus `previousStatus` / `newStatus` where relevant.',
            `Webhooks fire for every ticket on the tenant. Keep only your own by checking that \`origin\` is \`${source}\` (\`data.origin\` or \`data.entity.origin\`).`,
          ],
        },
        { type: 'heading', text: 'Verifying a delivery' },
        {
          type: 'table',
          head: ['Header', 'Content'],
          rows: [
            ['`X-Webhook-Id`', 'Unique delivery id'],
            ['`X-Webhook-Event`', 'Event name'],
            ['`X-Webhook-Timestamp-Unix`', 'Send time, unix seconds'],
            ['`X-Webhook-Signature-V2`', '`sha256=<hex>`'],
            ['`X-Webhook-Signature-V2-Prev`', 'Present only while a secret rotation is in progress'],
          ],
        },
        {
          type: 'list',
          ordered: true,
          items: [
            'Build the string `{X-Webhook-Timestamp-Unix}.{raw request body}`.',
            'Compute HMAC-SHA256 over it with your webhook secret (UTF-8), as lowercase hex, prefixed with `sha256=`.',
            'Compare with `X-Webhook-Signature-V2` in constant time. During a rotation, also accept a match on `X-Webhook-Signature-V2-Prev`.',
            'Reject the delivery if the timestamp is more than 5 minutes from your clock.',
          ],
        },
        { type: 'code', samples: samples.verifyWebhook() },
        {
          type: 'p',
          text: 'An older `X-Webhook-Signature` header (no timestamp) is also sent. Do not rely on it.',
        },
        { type: 'heading', text: 'Retries' },
        {
          type: 'p',
          text: 'A delivery that times out or returns a non-2xx status is retried: 3 attempts in total, 2 minutes and then 4 minutes apart. After that it is marked failed and stays visible in the delivery log.',
        },
      ],
    },
    {
      id: 'polling',
      title: 'Polling backstop',
      scopes: [ApiKeyPermission.TICKETS_READ],
      blocks: [
        {
          type: 'p',
          text: 'A webhook can be lost if your endpoint is down for longer than the retry window. Run a periodic poll as well:',
        },
        {
          type: 'code',
          samples: [
            {
              lang: 'http',
              code: `GET ${ctx.baseUrl}/api/tickets?externalSource=${source}&updatedSince={last updatedAt you saw}&sortBy=updatedAt&sortOrder=asc`,
            },
          ],
        },
        {
          type: 'p',
          text: 'Keep the last `updatedAt` you processed and send it as the next `updatedSince`. The filter is inclusive, so expect to see the last ticket again.',
        },
      ],
    },
    {
      id: 'errors',
      title: 'Errors',
      blocks: [
        {
          type: 'code',
          samples: [
            {
              lang: 'json',
              code: '{\n  "error": { "code": "…", "message": "…", "details": [ … ], "documentation_url": "…" },\n  "request_id": "…"\n}',
            },
          ],
        },
        {
          type: 'table',
          head: ['HTTP', '`error.code`', 'Meaning', 'Retry?'],
          rows: [
            ['400', '`validation_error`', 'Body or query invalid; `details` lists the fields. Also a wrong `externalSource` for the key', 'No, fix the request'],
            ['400', '`unsupported_api_version`', 'Unknown `Accept-Version`', 'No'],
            ['401', '`invalid_api_key`', 'Key missing, wrong, revoked or expired', 'No'],
            ['402', '`feature_not_entitled`', 'The tenant\'s plan does not include API access', 'No'],
            ['403', '`insufficient_scope`', 'The key lacks the scope', 'No'],
            ['403', '`route_not_available_to_api_keys`', 'The route is not part of the public API', 'No'],
            ['403', '`ip_not_allowed`', 'Caller IP is not on the tenant\'s allow-list', 'No'],
            ['403', '`forbidden`', 'Other refusals, including another product\'s `externalSource`', 'No'],
            ['404', '`not_found`', 'Not found in this tenant', 'No'],
            ['409', '`conflict`', 'Duplicate `externalId`', 'No, use the existing ticket'],
            ['413', '`payload_too_large`', 'Attachment over 25 MB', 'No'],
            ['429', '`rate_limit_exceeded`', 'Over the per-minute limit', 'Yes, after `Retry-After`'],
            ['500 / 503 / 504', '`internal_error` / `service_unavailable` / `gateway_timeout`', 'A problem on the CentraOne side', 'Yes, with backoff and the same `Idempotency-Key`'],
          ],
        },
        {
          type: 'p',
          text: 'Branch on the HTTP status and `error.code`. The `message` text is for people and may change. Quote `request_id` when you report a problem.',
        },
      ],
    },
    {
      id: 'go-live',
      title: 'Go-live checklist',
      blocks: [{ type: 'checklist' }],
    },
  ];

  return sections;
}

function teamBlocks(ctx: GuideContext): GuideBlock[] {
  const intro: GuideBlock = {
    type: 'p',
    text: 'Teams cannot be listed with an API key. Send the id of the team a ticket belongs to as `teamId` on create, or later with `PATCH /api/tickets/{id}/assign`.',
  };
  if (!ctx.teams) {
    return [intro, { type: 'p', text: 'The team list is not available here. Ask your CentraOne admin for the team ids.' }];
  }
  if (ctx.teams.length === 0) {
    return [intro, { type: 'p', text: 'This tenant has no teams yet. Without a `teamId`, tickets follow the pipeline\'s routing rules.' }];
  }
  return [
    intro,
    {
      type: 'table',
      head: ['Team', 'teamId'],
      copyColumn: 1,
      rows: ctx.teams.map((team) => [team.name, team.id]),
    },
  ];
}

/** Scopes a section needs that the selected key does not have. */
export function missingScopes(
  section: Pick<GuideSection, 'scopes'>,
  keyScopes: readonly string[] | null | undefined,
): ApiKeyPermission[] {
  if (!keyScopes || !section.scopes) return [];
  return section.scopes.filter((scope) => !keyScopes.includes(scope));
}
